import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2 } from 'lucide-react';
import { DataTable } from '../../components/ui/data-table';

const WorkProceduresPage = () => {
  const [procedures, setProcedures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ name: '', code: '', description: '', estimated_hours: '', steps: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/procedures');
      setProcedures(response.data);
    } catch (error) {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        steps: formData.steps ? formData.steps.split('\n').filter(s => s.trim()) : []
      };
      if (editItem) {
        await api.put(`/procedures/${editItem.id}`, payload);
        toast.success('Procedura actualizata!');
      } else {
        await api.post('/procedures', payload);
        toast.success('Procedura creata!');
      }
      setShowDialog(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({ name: item.name, code: item.code || '', description: item.description || '', estimated_hours: item.estimated_hours || '', steps: (item.steps || []).join('\n') });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sigur doriti sa stergeti?')) return;
    try {
      await api.delete(`/procedures/${id}`);
      toast.success('Procedura stearsa!');
      fetchData();
    } catch (error) {
      toast.error('Eroare la stergere');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', code: '', description: '', estimated_hours: '', steps: '' });
  };

  const proceduresData = procedures.map(p => ({
    ...p,
    hours_display: p.estimated_hours ? `${p.estimated_hours}h` : '-',
    steps_count: p.steps?.length || 0
  }));

  const tableColumns = [
    { key: 'code', label: 'Cod', sortable: true, filterable: true, width: 'w-24',
      render: (value) => value ? <Badge variant="outline" className="font-mono">{value}</Badge> : <span className="text-slate-400">-</span>
    },
    {
      key: 'name', label: 'Denumire Procedura', sortable: true, filterable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    { key: 'description', label: 'Descriere', sortable: false, filterable: true,
      render: (value) => <span className="text-slate-500 truncate max-w-[250px] block">{value || '-'}</span>
    },
    { key: 'hours_display', label: 'Ore Est.', sortable: true, filterable: false, width: 'w-24' },
    { key: 'steps_count', label: 'Pasi', sortable: true, filterable: false, width: 'w-20',
      render: (value) => <Badge variant="secondary">{value}</Badge>
    },
    {
      key: 'actions', label: 'Actiuni', sortable: false, filterable: false, width: 'w-24',
      render: (_, row) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}><Edit className="w-4 h-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6" data-testid="procedures-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Proceduri de Lucru</h1>
          <p className="text-slate-600 mt-1">Gestioneaza procedurile standard ({procedures.length} total)</p>
        </div>
        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) { setEditItem(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button className="btn-primary h-12"><Plus className="w-5 h-5 mr-2" />PROCEDURA NOUA</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editItem ? 'Editeaza Procedura' : 'Adauga Procedura'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cod</Label>
                  <Input value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })} className="h-11" />
                </div>
                <div className="space-y-2">
                  <Label>Ore Estimate</Label>
                  <Input type="number" value={formData.estimated_hours} onChange={(e) => setFormData({ ...formData, estimated_hours: e.target.value })} className="h-11" min="0" step="0.5" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Denumire *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required className="h-11" />
              </div>
              <div className="space-y-2">
                <Label>Descriere</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Pasi (un pas pe linie)</Label>
                <Textarea value={formData.steps} onChange={(e) => setFormData({ ...formData, steps: e.target.value })} rows={5} placeholder="Pas 1: ...\nPas 2: ..." />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Anuleaza</Button>
                <Button type="submit" className="btn-primary">{editItem ? 'ACTUALIZEAZA' : 'ADAUGA'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0052CC]"></div>
        </div>
      ) : (
        <DataTable data={proceduresData} columns={tableColumns} pageSize={10} searchable={true} exportable={true} printable={true} selectable={true} onRowClick={handleEdit} emptyMessage="Nu exista proceduri" />
      )}
    </div>
  );
};

export default WorkProceduresPage;
